import React, { useState } from 'react';
import { Plus, Trash2, Eye, EyeOff } from 'lucide-react';
import { Variable } from '../../stores';

interface VariableEditorProps {
  variables: Variable[];
  onChange: (variables: Variable[]) => void;
}

const emptyVariable = (): Variable => ({
  key: '',
  value: '',
  isSecret: false,
  enabled: true,
});

export function VariableEditor({ variables, onChange }: VariableEditorProps) {
  // Track which secrets are revealed (by index)
  const [revealedSecrets, setRevealedSecrets] = useState<Set<number>>(new Set());

  const addVariable = () => {
    onChange([...variables, emptyVariable()]);
  };

  const updateVariable = (index: number, updates: Partial<Variable>) => {
    const newVariables = [...variables];
    newVariables[index] = { ...newVariables[index], ...updates };
    onChange(newVariables);
  };

  const removeVariable = (index: number) => {
    onChange(variables.filter((_, i) => i !== index));
    // Clean up revealed state
    setRevealedSecrets((prev) => {
      const next = new Set(prev);
      next.delete(index);
      // Adjust indices for items after the removed one
      const adjusted = new Set<number>();
      next.forEach((i) => {
        if (i > index) {
          adjusted.add(i - 1);
        } else {
          adjusted.add(i);
        }
      });
      return adjusted;
    });
  };

  const toggleReveal = (index: number) => {
    setRevealedSecrets((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const isRevealed = (index: number) => revealedSecrets.has(index);

  return (
    <div className="space-y-1">
      {/* Header */}
      <div className="grid grid-cols-[24px_1fr_1fr_60px_32px_32px] gap-2 text-xs font-medium text-muted-foreground px-1 pb-1">
        <div></div>
        <div>Key</div>
        <div>Value</div>
        <div className="text-center">Secret</div>
        <div></div>
        <div></div>
      </div>

      {/* Rows */}
      {variables.map((variable, index) => (
        <div
          key={index}
          className={`grid grid-cols-[24px_1fr_1fr_60px_32px_32px] gap-2 items-center group ${
            !variable.enabled ? 'opacity-50' : ''
          }`}
        >
          {/* Enable Checkbox */}
          <input
            type="checkbox"
            checked={variable.enabled}
            onChange={(e) => updateVariable(index, { enabled: e.target.checked })}
            className="w-4 h-4 rounded border-input cursor-pointer"
            title={variable.enabled ? 'Disable variable' : 'Enable variable'}
          />

          {/* Key Input */}
          <input
            type="text"
            value={variable.key}
            onChange={(e) => updateVariable(index, { key: e.target.value })}
            placeholder="VARIABLE_NAME"
            className="px-3 py-1.5 text-sm bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring font-mono"
          />

          {/* Value Input */}
          <div className="relative flex items-center">
            <input
              type={variable.isSecret && !isRevealed(index) ? 'password' : 'text'}
              value={variable.value}
              onChange={(e) => updateVariable(index, { value: e.target.value })}
              placeholder={variable.isSecret ? '••••••••' : 'value'}
              className="w-full px-3 py-1.5 text-sm bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring font-mono pr-8"
            />
            {variable.isSecret && (
              <button
                type="button"
                onClick={() => toggleReveal(index)}
                className="absolute right-2 p-0.5 text-muted-foreground hover:text-foreground transition-colors"
                title={isRevealed(index) ? 'Hide value' : 'Show value'}
              >
                {isRevealed(index) ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </button>
            )}
          </div>

          {/* Secret Toggle */}
          <div className="flex justify-center">
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={variable.isSecret}
                onChange={(e) => {
                  updateVariable(index, { isSecret: e.target.checked });
                  // Reset reveal state when toggling secret off
                  if (!e.target.checked) {
                    setRevealedSecrets((prev) => {
                      const next = new Set(prev);
                      next.delete(index);
                      return next;
                    });
                  }
                }}
                className="sr-only peer"
              />
              <div className="w-8 h-4 bg-muted rounded-full peer peer-checked:bg-amber-500 transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:after:translate-x-4" />
            </label>
          </div>

          {/* Placeholder for alignment */}
          <div></div>

          {/* Delete Button */}
          <button
            type="button"
            onClick={() => removeVariable(index)}
            className="p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-destructive/10 hover:text-destructive transition-all"
            title="Remove variable"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ))}

      {/* Add Row Button */}
      <button
        type="button"
        onClick={addVariable}
        className="flex items-center gap-1 px-2 py-1.5 text-sm text-muted-foreground hover:text-foreground hover:bg-accent rounded transition-colors mt-2"
      >
        <Plus className="w-4 h-4" />
        Add Variable
      </button>

      {/* Empty state */}
      {variables.length === 0 && (
        <div className="text-center py-8 text-muted-foreground">
          <p className="text-sm">No variables yet.</p>
          <p className="text-xs mt-1">Click "Add Variable" to create one.</p>
        </div>
      )}
    </div>
  );
}
